"use client";

import { ArrowRight, ChevronDown } from "lucide-react";

import { tourPageSurfaceProps, type DepartureDto } from "@tripcart/shared";
import { Button } from "@tripcart/traveler-ui";
import {
  Collapsible,
  CollapsiblePanel,
  CollapsibleTrigger,
} from "@tripcart/traveler-ui/components/collapsible";

import { ExternalBookButton } from "@/components/external-book-button";
import { useFormatMoney } from "@/lib/currency-format-context";
import { useFormatDate } from "@/lib/datetime-format-context";
import { WhatsappMark } from "@/components/whatsapp-mark";
import type { PluralTranslator, Translator } from "@/lib/translations";

import { useExternalBooking } from "../_external-booking/external-booking-context";
import { PriceFigure } from "../_price-figure";
import {
  isBookable,
  isGuaranteedToRun,
  resolveDepartureStatus,
} from "./departure-status";
import {
  AMOUNT_SLOT,
  EmphasisedAmount,
  toIsoDate,
} from "./departure-row-helpers";
import {
  departureChatUrl,
  DepartureDetails,
  hasDepartureDetails,
  type DepartureExtras,
  type DepartureFacts,
} from "./departure-details";
import { DateTile } from "./date-tile";
import styles from "./departure-row.module.css";
import { PromiseBadges, StatusPill } from "./status-pill";

/**
 * The guide's layout 12, "banner rows": the price sits on its own tinted panel
 * on the same centre line as the two actions, and the disclosure is a
 * full-width bar rather than a text link — on a card this wide a link
 * disappears.
 *
 * The card shell (`.dsh`) owns the frame: border, radius, background, hover and
 * state. The body only lays its own content out, so no layout can end up
 * double-bordered.
 */
interface DepartureRowProps {
  departure: DepartureDto;
  /** End date of the trip, pre-computed from duration. Null when the tour
   *  carries no duration — the row then shows the start date alone rather than
   *  inventing a return. */
  endDate: Date | null;
  selected: boolean;
  expanded: boolean;
  onToggleDetails: () => void;
  onBook: () => void;
  /** Deposit percent from the operator's payment policy, 0–100. Null when they
   *  have configured none — the "pay X today" line is then omitted rather than
   *  asserting a default. */
  depositPercent: number | null;
  /** Everything the expanded panel shows. Each field is independently
   *  optional; see `DepartureFacts`. */
  facts: DepartureFacts;
  /** The operator's own detail lines and the two WhatsApp surfaces. Each
   *  surface is absent unless it should render, so this row never re-derives
   *  "switched on, and is there a number" — see `DepartureExtras`.
   *
   *  This replaced a `whatsappUrl: string | null` that carried ONE pre-built
   *  link for the whole page. The number is now the tour page's own and the
   *  greeting keeps its `{date}` placeholder, so the link is built per row. */
  extras?: DepartureExtras | undefined;
  /** The operator's own smallest runnable party, from the active package's
   *  `minParticipants`. Null when they set none — no badge then. */
  minParticipants: number | null;
  /** False for an on-request tour — see `PromiseBadges`. */
  instantConfirmation: boolean;
  formatDate: (iso: string) => string;
  tn: PluralTranslator;
  t: Translator;
  /** Quote-on-request (AC-10): this tour has no fixed price, so this row
   *  quotes nothing. Hides the price figure and the deposit line (itself a
   *  price-derived figure) while the date, end date, and availability status
   *  stay — the traveler still needs to see WHICH dates run. Also drops the
   *  WhatsApp chat mark: AC-15 wants exactly one way to ask about price, and
   *  that is the quote box in the sidebar, not a per-row chat link. */
  isOnRequest?: boolean;
  /** Replaces "Book now" when `isOnRequest` is true — the same handoff
   *  `_package-card.tsx` uses, scrolling to the booking sidebar's quote box,
   *  which holds the actual form/custom-link/contact action for this tour. */
  onRequestQuote?: () => void;
}

export function DepartureRow({
  departure,
  endDate,
  selected,
  expanded,
  onToggleDetails,
  onBook,
  depositPercent,
  facts,
  extras,
  minParticipants,
  instantConfirmation,
  formatDate,
  tn,
  t,
  isOnRequest,
  onRequestQuote,
}: DepartureRowProps) {
  const formatMoney = useFormatMoney();
  const formatDateWithOpts = useFormatDate();
  const externalBooking = useExternalBooking();
  const status = resolveDepartureStatus(departure);
  const bookable = isBookable(status);
  const soldOut = status.kind === "sold-out";
  // Enough travellers have already booked to meet the operator's minimum party,
  // so this date departs whatever happens next. The booked count is the row's
  // own `capacity − spotsLeft`; see `isGuaranteedToRun` for every case that
  // answers false rather than guessing.
  //
  // `minParticipants`, NOT `facts.groupSize.min`. This read `groupSize.min`
  // until 2026-09-17 and was wrong in the worst way — quietly, on real tours.
  // `public-experience.payload.ts:1244` builds `groupSize` as the literal
  // `{ min: 1, max: 10 }` and is its only producer, so the badge reduced to
  // `capacity − spotsLeft >= 1`: every capped, open departure that had sold ONE
  // seat claimed it was guaranteed to run. `minParticipants` is the operator's
  // own figure and is null when they never set one.
  const guaranteed = isGuaranteedToRun(departure, minParticipants);
  // No button at all when there is nothing behind it. An operator who has set
  // no payment policy and added no departure details of their own would
  // otherwise get a "See more details" that expands onto an empty box.
  const hasDetails = hasDepartureDetails({ facts, extras });
  // A sold-out date makes no promises, so it reserves no ribbon room and the
  // body starts higher. `no-rib` in the guide. This must stay in step with
  // `PromiseBadges`' own conditions or the card pads for a ribbon that never
  // arrives — which is why it is the same OR, not just `instantConfirmation`:
  // an on-request tour that has already filled its minimum still has the green
  // line to show.
  const hasRibbon = !soldOut && (guaranteed || instantConfirmation);

  const start = new Date(`${departure.date}T00:00:00`);
  // A same-year range shows the year once, on the end date — "Aug 15 → Aug
  // 31, 2026", not "Aug 15, 2026 → Aug 31, 2026".
  const sameYearRange =
    endDate !== null && start.getFullYear() === endDate.getFullYear();
  // `> 0`, not `!= null`. `depositPercent` is `min(0).max(100)`, so ZERO is a
  // real and meaningful policy — "reserve now, pay later" — and the booking
  // rail on this same screen branches on `> 0` for exactly that reason. Read
  // as `!= null` this line rendered "Pay $0.00 per traveler today to hold this
  // date" beside a rail saying "Pay nothing today", for the same operator, on
  // one page.
  const deposit =
    depositPercent != null && depositPercent > 0 && bookable
      ? Math.round((departure.priceFrom.amount * depositPercent) / 100)
      : null;

  return (
    <div
      // Overflow is clipped on the PHONE so the ribbon band clips to the card's
      // top corners, and visible from `sm` because from there the ribbon is a
      // pill that deliberately straddles the same edge and would be cut in half
      // by it. A selected card takes a 1px brand ring; its border stays the
      // resting hairline.
      className={styles.card}
      data-selected={selected ? "" : undefined}
      data-sold-out={soldOut ? "" : undefined}
      // In flow on the phone, so the ribbon needs no reserved room; a floating
      // pill at `sm` still does.
      data-ribbon={hasRibbon ? "" : undefined}
    >
      {hasRibbon ? (
        <PromiseBadges
          status={status}
          guaranteed={guaranteed}
          instantConfirmation={instantConfirmation}
          t={t}
        />
      ) : null}

      {/* A rounded top, not a flat one: the card's radius less 1px, an inset
          under the card's own corner. It is needed only from `sm`, where the card turns
          overflow-visible for the ribbon and stops clipping this box itself.
          Below `sm` the card clips its own corners, so a ribbon-less row showed
          a sliver of card between two curves at 390px when this rounded too. */}
      <div className={styles.body}>
        {/* Left: date tile, the range, status, and the deposit line. The phone
            runs the mobile guide's `.dsh-b` gutters — 13px, not 20px. At 358px
            a 20px gutter on a card that is itself inset 16px leaves the date
            range 36px from the screen edge and wrapping a line earlier than it
            needs to. */}
        <div className={styles.lead}>
          <DateTile date={start} dimmed={soldOut} />
          <div className={styles.leadText}>
            <div className={styles.range}>
              <span className={styles.rangeInner}>
                {sameYearRange
                  ? formatDateWithOpts(departure.date, {
                      weekday: "short",
                      monthStyle: "short",
                      withYear: false,
                    })
                  : formatDate(departure.date)}
                {endDate ? (
                  <>
                    <ArrowRight
                      aria-hidden
                      // The subtle tone clears the 3:1 non-text floor. This
                      // arrow carries meaning — it is what separates the start
                      // date from the return — so it is not decoration.
                      className={styles.arrow}
                      strokeWidth={2}
                    />
                    {formatDate(toIsoDate(endDate))}
                  </>
                ) : null}
              </span>
            </div>
            <div className={styles.status}>
              <StatusPill status={status} tn={tn} t={t} />
            </div>
            {deposit !== null ? (
              <p className={styles.deposit}>
                <EmphasisedAmount
                  sentence={t(
                    "Pay {amount} per traveler today to hold this date.",
                    {
                      amount: AMOUNT_SLOT,
                    },
                  )}
                  amount={formatMoney(deposit)}
                />
              </p>
            ) : null}
          </div>
        </div>

        {/* Right: the price panel — beside the date from `sm`, under it below.
            Below `sm` it stops being a PANEL: the mobile guide's `.dsh-p` is a
            hairline-topped row on the card's own background, left-aligned on
            the same edge as everything above it, with the actions on their own
            row under it. A tinted, centred block reads as a second card inside
            the first at 358px, and centring the figure breaks the left margin
            every other line on the card shares. */}
        {/* A top padding on BOTH branches. The ribbon branch used to supply
            only a desktop one, with no base — so below 640px, where the grid
            stacks to one column, the price panel got `padding-top: 0` and its
            figure sat flush against the panel's own top border. That is the
            common case on a phone: `hasRibbon` is true for every open,
            non-on-request departure. The extra room the ribbon needs is a
            DESKTOP concern — on mobile the panel is never underneath it. */}
        <div className={styles.price}>
          {/* AC-10: an on-request, open date still says "Sold out" when it
              is — that is availability, not price — but otherwise shows
              nothing here at all; the quote box is the one place this tour
              states what it costs. */}
          {soldOut || !isOnRequest ? (
            <div className={styles.priceText}>
              {soldOut ? (
                <span className={styles.soldOut}>{t("Sold out")}</span>
              ) : (
                // Guide `.lg-fig` + `.lg-cur` — same relationship as the rail's
                // figure, one size band down (21px / 13px). The phone drops to
                // the mobile guide's `.dsh-fig`, 19px.
                <PriceFigure
                  formatted={formatMoney(departure.priceFrom.amount)}
                  className={styles.figure}
                  affixClassName={styles.figureAffix}
                />
              )}
            </div>
          ) : null}
          {/* Guide `.bn-cta` — the WhatsApp mark sits BESIDE Book now, not
              instead of it. Someone who is nearly ready but has one question
              about this specific date needs an out that is not "leave the
              page"; the link carries the date so the operator does not have to
              ask which one.

              Its own switch, in Tours → Configuration → Tour Page → WhatsApp
              Button. Until 2026-09-17 this drew off the WhatsApp Widget app's
              single agency-wide link, which meant it could not be offered
              without also offering the details-panel line, and uninstalling an
              unrelated app silently removed it. */}
          {bookable ? (
            // The guide's `.dsh-acts`: on a phone the mark keeps its 42px
            // square and Book now takes the whole rest of the row, so the one
            // thing the card is for is the full width of the card. From `sm`
            // the pair sits centred in the price panel at its natural width.
            <div className={styles.actions}>
              {/* AC-15: on-request wants exactly one way to ask about price
                  — the quote box's own button — so this row drops the
                  per-departure WhatsApp chat link rather than offering a
                  second channel beside "Request a Quote". */}
              {extras?.whatsappButton && !isOnRequest ? (
                <a
                  // The WhatsApp Button card's Preview deep-link
                  // (`?highlight=whatsapp`) looks for this attribute. It
                  // existed only on the details-panel line, which renders
                  // solely when the CONTACT surface is on — so an operator who
                  // enabled just this button clicked "See it on your site" and
                  // the highlighter polled, found nothing, and gave up
                  // silently, leaving a random departure expanded and nothing
                  // ringed. The card is named after this button; it has to be
                  // findable.
                  {...tourPageSurfaceProps("whatsapp")}
                  href={departureChatUrl(
                    extras.whatsappButton,
                    extras,
                    formatDate(departure.date),
                  )}
                  target="_blank"
                  rel="noopener"
                  aria-label={t("Ask about the {date} departure on WhatsApp", {
                    date: formatDate(departure.date),
                  })}
                  className={styles.whatsapp}
                >
                  <WhatsappMark className={styles.whatsappMark} />
                </a>
              ) : null}
              {isOnRequest ? (
                // AC-10: "the quote box's button replaces 'Book Now'" — this
                // sends the traveler to that same button in the sidebar
                // rather than opening a form of its own.
                <Button
                  variant="book"
                  size="lg"
                  className={styles.book}
                  onClick={onRequestQuote}
                >
                  {t("Request a Quote")}
                </Button>
              ) : externalBooking ? (
                <ExternalBookButton
                  booking={externalBooking}
                  className={styles.book}
                />
              ) : (
                <Button
                  variant="book"
                  size="lg"
                  className={styles.book}
                  onClick={onBook}
                >
                  {t("Book now")}
                </Button>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {/* Full-width disclosure bar — absent entirely when the panel would open
          onto nothing. */}
      {hasDetails ? (
        <Collapsible open={expanded} onOpenChange={onToggleDetails}>
          <div className={styles.disclosure}>
            {/* `data-departure-details-toggle`: the dashboard's Preview deep-links
              to two surfaces that live INSIDE this panel, so its highlighter
              needs a handle to open it before it can point at them. */}
            <CollapsibleTrigger
              data-departure-details-toggle=""
              className={styles.toggle}
            >
              {expanded ? t("Hide details") : t("See more details")}
              <ChevronDown
                aria-hidden
                className={styles.chevron}
                strokeWidth={2}
              />
            </CollapsibleTrigger>
          </div>
          <CollapsiblePanel>
            <DepartureDetails
              priceAmount={departure.priceFrom.amount}
              startIso={departure.date}
              facts={facts}
              extras={extras}
              formatDate={formatDate}
              t={t}
            />
          </CollapsiblePanel>
        </Collapsible>
      ) : null}
    </div>
  );
}
